import { useMemo } from 'react';
import { useMdMediaQuery } from 'utils/responsiveness';
import { bigNumberToStr } from 'utils/strings';
import { Column } from 'react-table';
import Tokens from 'components/core/tokens';
import TokenValues from 'components/core/token-values';
import Table from 'components/core/table';
import { PoolModel } from 'models/portfolio/PoolModel';
import { SupplyTokenModel } from 'models/portfolio/SupplyTokenModel';
import EscrowingCards from './EscrowingCards';
import { VeTokenModel } from 'models/portfolio/VeTokenModel';
import { ReactComponent as HideSvg } from 'assets/svg/hide.svg';
import { ReactComponent as ShowSvg } from 'assets/svg/show.svg';
import UnlockScheduleTable from './unlock-schedule-table';

const EscrowingTable = ({ poolInfo }: { poolInfo: PoolModel[] }) => {
  const columns = useMemo<Column<PoolModel>[]>(
    () => [
      {
        Header: 'Pool',
        accessor: 'type',
        Cell: ({ row }) => <Tokens tokens={row.original.supplyTokenList} />,
        disableSortBy: true,
      },
      {
        Header: 'Votes Balance',
        accessor: 'veTokenList',
        Cell: ({ row }) => (
          <TokenValues
            tokens={row.original.veTokenList?.map((token: VeTokenModel) => ({
              amount: token.amount,
              symbol: token.symbol,
            }))}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: 'Tokens Balance',
        accessor: 'supplyTokenList',
        Cell: ({ row }) => (
          <TokenValues
            tokens={row.original.supplyTokenList.map(
              (token: SupplyTokenModel) => ({
                amount: token.amount,
                symbol: token.symbol,
                usdValue: token.usdValue,
              })
            )}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: 'Unlock schedule',
        accessor: 'unlockSchedule',
        Cell: ({ row }) =>
          row.original.unlockSchedule &&
          row.original.unlockSchedule.length > 0 ? (
            <div className='flex items-center gap-x-1'>
              <span>{row.isExpanded ? 'Hide' : 'Show'}</span>
              {row.isExpanded ? (
                <HideSvg
                  className='w-4 h-4'
                  {...row.getToggleRowExpandedProps()}
                  title=''
                />
              ) : (
                <ShowSvg
                  className='w-4 h-4'
                  {...row.getToggleRowExpandedProps()}
                  title=''
                />
              )}
            </div>
          ) : (
            <span className='text-secondary-1'>No locked deposits</span>
          ),
        disableSortBy: true,
      },
      {
        Header: 'USD Value',
        accessor: 'totalUsdValue',
        Cell: ({ row }) => (
          <span className='font-bold'>
            ${bigNumberToStr(row.original.totalUsdValue, 0)}
          </span>
        ),
        sortType: (rowA, rowB) => {
          return rowA.original.totalUsdValue.comparedTo(
            rowB.original.totalUsdValue
          );
        },
        className: 'w-48',
      },
    ],
    []
  );

  const defaultSort = useMemo(() => ({ id: 'totalUsdValue', desc: true }), []);
  const isDesktop = useMdMediaQuery();

  return isDesktop ? (
    <Table
      columns={columns}
      data={poolInfo}
      title='Vote Escrowed'
      defaultSortBy={defaultSort}
      subComponent={(row) => <UnlockScheduleTable data={row.unlockSchedule} />}
    />
  ) : (
    <EscrowingCards poolInfo={poolInfo} />
  );
};

export default EscrowingTable;
